import React from 'react';
import { Typography, Chip } from '@material-ui/core';

export interface AvailableToken {
  token: string;
  title: string;
  description: string;
  example: string;
  calculation: string;
}

interface TokenCardProps {
  tokenItem: AvailableToken;
  lightMode: boolean;
  onTokenClick: (token: string, requiresInput: boolean) => void;
}

const TokenCard: React.FC<TokenCardProps> = ({ tokenItem, lightMode, onTokenClick }) => {
  // Keyboard handler for accessibility
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTokenClick(tokenItem.token, tokenItem.token.includes('| minutesPerCall>'));
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      style={{
        border: lightMode ? "1px solid #ddd" : "1px solid #555",
        borderRadius: "6px",
        padding: "10px",
        backgroundColor: lightMode ? "white" : "#333",
        cursor: "pointer",
        transition: "all 0.2s ease",
        height: "fit-content",
        minHeight: 0,
        overflow: "hidden"
      }}
      onClick={() => onTokenClick(tokenItem.token, tokenItem.token.includes('| minutesPerCall>'))}
      onKeyDown={handleKeyDown}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = lightMode ? "#f0f0f0" : "#444";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = lightMode ? "white" : "#333";
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px", flexShrink: 0 }}>
        <Typography variant="subtitle2" style={{ 
          fontWeight: "bold", 
          color: lightMode ? "#1976d2" : "#64b5f6", 
          fontSize: "0.9rem",
          flex: 1,
          marginRight: "8px",
          lineHeight: "1.2"
        }}>
          {tokenItem.title}
        </Typography>
        <Chip 
          label={tokenItem.token} 
          size="small" 
          style={{ 
            backgroundColor: lightMode ? "#e3f2fd" : "#1e3a5f",
            color: lightMode ? "#1976d2" : "#64b5f6",
            fontFamily: "monospace",
            fontSize: "0.65rem",
            flexShrink: 0,
            maxWidth: "120px",
            height: "20px"
          }}
        />
      </div>
      <Typography variant="body2" style={{ 
        color: lightMode ? "#666" : "#ccc", 
        marginBottom: "6px", 
        fontSize: "0.8rem",
        wordBreak: "break-word"
      }}>
        {tokenItem.description}
      </Typography>
      
      {/* Calculation explanation */}
      <div style={{
        backgroundColor: lightMode ? "#f8f9fa" : "#2a2a2a",
        padding: "6px",
        borderRadius: "4px",
        marginBottom: "6px",
        border: lightMode ? "1px solid #e9ecef" : "1px solid #444",
        flexShrink: 0
      }}>
        <Typography variant="caption" style={{ 
          color: lightMode ? "#666" : "#ccc",
          fontFamily: "monospace",
          fontSize: "0.7rem",
          wordBreak: "break-word"
        }}>
          {tokenItem.calculation}
        </Typography>
      </div>
      
      <Typography variant="caption" style={{ 
        color: lightMode ? "#888" : "#999", 
        fontStyle: "italic",
        fontFamily: "monospace",
        backgroundColor: lightMode ? "#f8f8f8" : "#2a2a2a",
        padding: "3px 6px",
        borderRadius: "3px",
        display: "block",
        fontSize: "0.7rem",
        wordBreak: "break-word"
      }}>
        Example: {tokenItem.example}
      </Typography>
    </div>
  );
};

export default TokenCard;